export type BrandSpyTab = "competitors" | "ads" | "favorites" | "landing-pages" | "changes";
export type BrandSpySort = "newest" | "oldest" | "last-seen" | "status" | "platform-start";
export type BrandSpyCompetitorView = "active" | "all";
export type BrandSpyUrlState = {
  tab: BrandSpyTab; competitorId?: string; page: number; search: string; sort: BrandSpySort;
  competitorView: BrandSpyCompetitorView;
  status: string; format: string; from: string; to: string; range: string; missing: boolean; duplicates: boolean;
  mediaAvailability: string; productionEligibility: string; durationMin: string; durationMax: string; discoveredFrom: string;
};

export function filterSpyCompetitors<T extends { archivedAt?: string | null }>(
  items: T[],
  view: BrandSpyCompetitorView,
): T[] {
  return view === "all" ? items : items.filter((competitor) => !competitor.archivedAt);
}

const defaults: BrandSpyUrlState = {
  tab: "competitors", competitorView: "active", page: 1, search: "", sort: "newest", status: "all",
  format: "all", from: "", to: "", range: "since-last-success", missing: false, duplicates: false,
  mediaAvailability: "all", productionEligibility: "all", durationMin: "", durationMax: "", discoveredFrom: "",
};
const tabs: BrandSpyTab[] = ["competitors", "ads", "favorites", "landing-pages", "changes"];
const listSort: Record<BrandSpySort, "newest" | "oldest" | "observed" | "status" | "platform_start"> = {
  newest: "newest",
  oldest: "oldest",
  "last-seen": "observed",
  status: "status",
  "platform-start": "platform_start",
};

/** Serializes only non-secret browsing state; URLSearchParams performs encoding. */
export function parseBrandSpyUrlState(search: string): BrandSpyUrlState {
  const p = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const tab = tabs.includes(p.get("tab") as BrandSpyTab) ? p.get("tab") as BrandSpyTab : defaults.tab;
  const sortValues: BrandSpySort[] = ["newest", "oldest", "last-seen", "status", "platform-start"];
  return {
    tab, competitorId: p.get("competitor") || undefined,
    competitorView: p.get("view") === "all" ? "all" : defaults.competitorView,
    page: Math.max(1, Number.parseInt(p.get("page") || "1", 10) || 1),
    search: p.get("q") || "", sort: sortValues.includes(p.get("sort") as BrandSpySort) ? p.get("sort") as BrandSpySort : defaults.sort,
    status: p.get("status") || "all", format: p.get("format") || "all",
    from: p.get("from") || "", to: p.get("to") || "", range: p.get("range") || defaults.range,
    missing: p.get("missing") === "1", duplicates: p.get("duplicates") === "1",
    mediaAvailability: p.get("media") || "all", productionEligibility: p.get("eligibility") || "all",
    durationMin: p.get("durationMin") || "", durationMax: p.get("durationMax") || "",
    discoveredFrom: p.get("discoveredFrom") || "",
  };
}

export function serializeBrandSpyUrlState(state: BrandSpyUrlState): string {
  const p = new URLSearchParams();
  const add = (key: string, value: string | number | boolean | undefined, initial?: string | number | boolean) => {
    if (value !== undefined && value !== "" && value !== initial && value !== false) {
      p.set(key, value === true ? "1" : String(value));
    }
  };
  add("tab", state.tab, defaults.tab); add("competitor", state.competitorId); add("page", state.page, defaults.page);
  add("view", state.competitorView, defaults.competitorView);
  add("q", state.search); add("sort", state.sort, defaults.sort); add("status", state.status, defaults.status);
  add("format", state.format, defaults.format); add("from", state.from); add("to", state.to);
  add("range", state.range, defaults.range); add("missing", state.missing); add("duplicates", state.duplicates);
  add("media", state.mediaAvailability, defaults.mediaAvailability);
  add("eligibility", state.productionEligibility, defaults.productionEligibility);
  add("durationMin", state.durationMin); add("durationMax", state.durationMax);
  add("discoveredFrom", state.discoveredFrom);
  return p.toString();
}

/** Maps shareable UI state to the public list contract. Date ranges are facts
 * about event history, so they are intentionally never sent to the ads list. */
export function brandSpyListQuery(
  state: BrandSpyUrlState,
  view: "ads" | "favorites" | "changes",
  verifiedLastSuccessfulSyncAt?: string | null,
): string {
  const p = new URLSearchParams({ page: String(state.page), pageSize: "25" });
  if (state.competitorId) p.set("spyBrandId", state.competitorId);
  if (state.search) p.set("search", state.search);
  if (view === "changes") {
    if (state.status !== "all") p.set("status", state.status);
    if (state.from) p.set("since", state.from);
    else if (state.range === "since-last-success" && verifiedLastSuccessfulSyncAt) {
      p.set("since", verifiedLastSuccessfulSyncAt);
    }
    if (state.to) p.set("until", state.to);
    return p.toString();
  }
  if (state.status !== "all") p.set("status", state.status);
  if (state.format !== "all") p.set("format", state.format);
  if (state.missing) p.set("missing", "true");
  if (state.duplicates) p.set("duplicates", "true");
  if (state.mediaAvailability !== "all") p.set("mediaAvailability", state.mediaAvailability);
  if (state.productionEligibility !== "all") p.set("productionEligibility", state.productionEligibility);
  if (state.durationMin) p.set("videoDurationMin", state.durationMin);
  if (state.durationMax) p.set("videoDurationMax", state.durationMax);
  if (state.discoveredFrom) p.set("discoveredFrom", state.discoveredFrom);
  p.set("sort", listSort[state.sort]);
  return p.toString();
}